import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Grid,
  Paper,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Business as BusinessIcon,
  Merge as MergeIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { editeursAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  formatEntityName,
  getSimilarityColor,
  getSimilarityThresholdMarks,
  entityConfigs,
  generateMergeOptions,
  validateMergeParams,
  prepareMergeData,
  getMergeMessage,
  getCacheKeys
} from '../utils/mergeUtils';

function DoublonsEditeursPage() {
  const { t } = useTranslation();
  const [threshold, setThreshold] = useState(0.8);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [mergeDialog, setMergeDialog] = useState({ open: false, group: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, editeur: null });
  const queryClient = useQueryClient();
  
  const config = entityConfigs.editeurs;

  // Requête pour récupérer les doublons
  const { data: duplicatesData, isLoading, error, refetch } = useQuery(
    ['editeurs-duplicates', threshold],
    () => editeursAPI.getDuplicates(threshold).then(res => res.data),
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000
    }
  );

  // Mutation pour fusionner les éditeurs
  const mergeMutation = useMutation(
    ({ targetId, sourceIds }) => {
      return Promise.all(
        sourceIds.map(sourceId => 
          editeursAPI.merge(targetId, sourceId)
        )
      );
    },
    {
      onSuccess: () => {
        toast.success(t('duplicatesEditeursPage.mergeSuccess'));
        getCacheKeys('editeurs').forEach(key => {
          queryClient.invalidateQueries(key);
        });
        setMergeDialog({ open: false, group: null });
        setSelectedGroup(null);
      },
      onError: (error) => {
        toast.error(t('duplicatesEditeursPage.mergeError'));
        console.error('Erreur fusion:', error);
      }
    }
  );

  // Mutation pour supprimer un éditeur
  const deleteMutation = useMutation(
    (editeurId) => editeursAPI.delete(editeurId),
    {
      onSuccess: () => {
        toast.success(t('duplicatesEditeursPage.deleteSuccess'));
        getCacheKeys('editeurs').forEach(key => {
          queryClient.invalidateQueries(key);
        });
        setDeleteDialog({ open: false, editeur: null });
      },
      onError: (error) => {
        toast.error(t('duplicatesEditeursPage.deleteError'));
        console.error('Erreur suppression:', error);
      }
    }
  );

  const handleMerge = (group) => {
    setMergeDialog({ open: true, group });
    setSelectedGroup(null);
  };

  const confirmMerge = () => {
    const validation = validateMergeParams(selectedGroup, mergeDialog.group);
    
    if (!validation.isValid) {
      toast.error(validation.error);
      return;
    }

    const { targetId, sourceIds } = prepareMergeData(selectedGroup, mergeDialog.group);
    mergeMutation.mutate({ targetId, sourceIds });
  };

  const handleDelete = (editeur) => {
    setDeleteDialog({ open: true, editeur });
  };

  const confirmDelete = () => {
    if (deleteDialog.editeur) {
      deleteMutation.mutate(deleteDialog.editeur.id);
    }
  };

  const formatAdditionalInfo = (editeur) => {
    const info = config.formatAdditionalInfo(editeur);
    return info.length > 0 ? info.join(' • ') : null;
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t('duplicatesEditeursPage.loadingError', { message: error.message })}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('duplicatesEditeursPage.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t('duplicatesEditeursPage.description')}
        </Typography>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('duplicatesEditeursPage.detectionSettings')}
          </Typography>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography gutterBottom>
                {t('duplicatesEditeursPage.similarityThreshold', { threshold: Math.round(threshold * 100) })}
              </Typography>
              <Slider
                value={threshold}
                onChange={(e, value) => setThreshold(value)}
                min={0.5}
                max={1.0}
                step={0.05}
                marks={getSimilarityThresholdMarks()}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                variant="outlined"
                onClick={() => refetch()}
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} /> : null}
              >
                {isLoading ? t('duplicatesEditeursPage.analyzing') : t('duplicatesEditeursPage.analyzeAgain')}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {duplicatesData?.data?.duplicates?.length === 0 ? (
            <Alert severity="success">
              {t('duplicatesEditeursPage.noDuplicates', { threshold: Math.round(threshold * 100) })}
            </Alert>
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6">
                  {t('duplicatesEditeursPage.duplicateGroups', { count: duplicatesData?.data?.total || 0 })}
                </Typography>
              </Box>

              {duplicatesData?.data?.duplicates?.map((duplicate, index) => (
                <Card key={index} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <BusinessIcon sx={{ mr: 1 }} />
                      <Typography variant="h6">
                        {t('duplicatesEditeursPage.duplicateGroup')}
                      </Typography>
                      <Chip
                        label={`${Math.round(duplicate.similarity * 100)}%`}
                        color={getSimilarityColor(duplicate.similarity)}
                        size="small"
                        sx={{ ml: 2 }}
                      />
                    </Box>

                    <List>
                      {duplicate.group.filter(editeur => editeur).map((editeur, editeurIndex) => (
                        <React.Fragment key={editeur.id}>
                          <ListItem>
                            <ListItemText
                              primary={formatEntityName(editeur)}
                              secondary={
                                <Box>
                                  <Typography variant="body2" color="text.secondary">
                                    {editeur[config.countField] || 0} {config.countLabel}
                                  </Typography>
                                  {formatAdditionalInfo(editeur) && (
                                    <Typography variant="body2" color="text.secondary">
                                      {formatAdditionalInfo(editeur)}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                color="error"
                                onClick={() => handleDelete(editeur)}
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                          {editeurIndex < duplicate.group.filter(editeur => editeur).length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  </CardContent>
                  <CardActions>
                    <Button
                      variant="contained"
                      startIcon={<MergeIcon />}
                      onClick={() => handleMerge(duplicate.group)}
                    >
                      {t('duplicatesEditeursPage.merge')}
                    </Button>
                  </CardActions>
                </Card>
              ))}
            </>
          )}
        </>
      )}

      {/* Dialog de fusion */}
      <Dialog
        open={mergeDialog.open}
        onClose={() => setMergeDialog({ open: false, group: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <MergeIcon sx={{ mr: 1 }} />
            {t('duplicatesEditeursPage.mergeTitle')}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('duplicatesEditeursPage.selectMainEditeur')}
          </Typography>
          
          <FormControl fullWidth>
            <InputLabel>{t('duplicatesEditeursPage.mainEditeur')}</InputLabel>
            <Select
              value={selectedGroup || ''}
              onChange={(e) => setSelectedGroup(e.target.value)}
            >
              {generateMergeOptions(mergeDialog.group, config).map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Alert severity="warning" sx={{ mt: 2 }}>
            {t('duplicatesEditeursPage.mergeWarning')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setMergeDialog({ open: false, group: null });
              setSelectedGroup(null);
            }}
            startIcon={<CloseIcon />}
          >
            {t('duplicatesEditeursPage.cancel')}
          </Button>
          <Button
            onClick={confirmMerge}
            variant="contained"
            startIcon={<CheckIcon />}
            disabled={!selectedGroup || mergeMutation.isLoading}
          >
            {mergeMutation.isLoading ? t('duplicatesEditeursPage.merging') : t('duplicatesEditeursPage.merge')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de suppression */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, editeur: null })}
      >
        <DialogTitle>{t('duplicatesEditeursPage.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('duplicatesEditeursPage.deleteConfirm', { name: formatEntityName(deleteDialog.editeur) })}
          </Typography>
          {deleteDialog.editeur?.[config.countField] > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('duplicatesEditeursPage.deleteWarning', { 
                count: deleteDialog.editeur[config.countField], 
                countLabel: config.countLabel 
              })}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, editeur: null })}
            startIcon={<CloseIcon />}
          >
            {t('duplicatesEditeursPage.cancel')}
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
            disabled={deleteMutation.isLoading}
          >
            {deleteMutation.isLoading ? t('duplicatesEditeursPage.deleting') : t('duplicatesEditeursPage.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DoublonsEditeursPage;
